import { bffRequestV3 } from '@/app/bffClient';
import { getAllPowersOfAttorney } from 'bff-api/mitt-malarenergi/v3';
import { getSelectedCustomer } from '@/app/slices/userSlice';
import { userIsNonCustomer } from '@/helpers/customerUtils';
import { State } from '@/types/State';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const usePowersOfAttorney = () => {
  const [hasPowersOfAttorney, setHasPowersOfAttorney] = useState(false);
  const queryClient = useQueryClient();
  const selectedCustomer = useSelector((state: State) =>
    getSelectedCustomer(state),
  );
  const [activePowersOfAttorney, setActivePowersOfAttorney] = useState<
    PowerOfAttorney[]
  >([]);
  const [inactivePowersOfAttorney, setInacticePowersOfAttorney] = useState<
    PowerOfAttorney[]
  >([]);
  const [newPowersOfAttorney, setNewPowersOfAttorney] = useState<
    PowerOfAttorney[]
  >([]);
  const queryKey = ['powersOfAttorney', selectedCustomer?.customerId];

  const formatDate = (date: string, replaceZeroWith: string) => {
    if (date === '1900-01-01T00:00:00Z') {
      return replaceZeroWith;
    }
    return dayjs(date).format('YYYY-MM-DD');
  };

  const formatPOA = (powerOfAttorney: PowerOfAttorney) => {
    powerOfAttorney.receivedStartDateTime = formatDate(
      powerOfAttorney.receivedStartDateTime,
      '–',
    );
    powerOfAttorney.responseStartDateTime = formatDate(
      powerOfAttorney.responseStartDateTime,
      '–',
    );
    powerOfAttorney.actualReportStartDateTime = formatDate(
      powerOfAttorney.actualReportStartDateTime,
      '–',
    );
    powerOfAttorney.actualReportEndDateTime = formatDate(
      powerOfAttorney.actualReportEndDateTime,
      'Tillsvidare',
    );

    return powerOfAttorney;
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        const envelope = await bffRequestV3((client) =>
          getAllPowersOfAttorney({
            client,
            path: { customerId: selectedCustomer?.customerId ?? 0 },
            throwOnError: true,
          }),
        );
        const data = envelope?.data?.[0] as PowerOfAttorney[];
        if (!data) {
          return [];
        }

        data.map((powerOfAttorney) => formatPOA(powerOfAttorney));
        return data;
      } catch {
        return [];
      }
    },
    enabled: !!selectedCustomer?.customerId && !userIsNonCustomer(selectedCustomer),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      setHasPowersOfAttorney(false);
    } else if (data.length > 0) {
      setHasPowersOfAttorney(true);
    }

    if (data) {
      groupData(data);
    }
  }, [data]);

  const updatePowerOfAttorney = (powerOfAttorney: PowerOfAttorney) => {
    queryClient.setQueryData(queryKey, (oldData: PowerOfAttorney[]) => {
      const updatedData = oldData.map((oldPOA) => {
        if (oldPOA.permissionId === powerOfAttorney.permissionId) {
          return formatPOA(powerOfAttorney);
        }
        return oldPOA;
      });
      return updatedData;
    });
  };

  const groupData = (data: PowerOfAttorney[]) => {
    setNewPowersOfAttorney([]);
    setActivePowersOfAttorney([]);
    setInacticePowersOfAttorney([]);
    data.forEach((powerOfAttorney) => {
      switch (powerOfAttorney.statusMP) {
        case 'Aktiv':
          setActivePowersOfAttorney((prev) => [...prev, powerOfAttorney]);
          break;
        case 'Inaktiv':
          setInacticePowersOfAttorney((prev) => [...prev, powerOfAttorney]);
          break;
        case 'Ny':
          setNewPowersOfAttorney((prev) => [...prev, powerOfAttorney]);
          break;
        case 'Väntande':
          setNewPowersOfAttorney((prev) => [...prev, powerOfAttorney]);
          break;
      }
    });
  };

  return {
    isLoading,
    hasPowersOfAttorney,
    activePowersOfAttorney,
    inactivePowersOfAttorney,
    newPowersOfAttorney,
    updatePowerOfAttorney,
  };
};

export default usePowersOfAttorney;
